import { useState, useCallback, useEffect, useRef } from "react";
import {
  ExtractedContract,
  SessionConfirmedData,
  BuildingCheckResponse,
} from "../../types/contract";

export const useContractManagement = (
  buildingChecks: Map<number, BuildingCheckResponse>,
  selectedContract: ExtractedContract | null,
  setSelectedContract: (contract: ExtractedContract | null) => void,
  setAggregatedContracts: (updater: (prev: ExtractedContract[]) => ExtractedContract[]) => void,
  setFinalContracts: (updater: (prev: ExtractedContract[]) => ExtractedContract[]) => void,
  flowState: string,
  finalContracts: ExtractedContract[]
) => {
  // 契約選択・確認関連の状態
  const [confirmedContracts, setConfirmedContracts] = useState<Set<number>>(new Set());
  const [needsReviewContracts, setNeedsReviewContracts] = useState<Set<number>>(new Set());
  const [appliedExistingDataContracts, setAppliedExistingDataContracts] = useState<Set<number>>(new Set());
  
  // 重複・競合解決関連の状態
  const [conflictResolutions, setConflictResolutions] = useState<Map<number, { 
    building: 'keep' | 'update' | null, 
    owner: 'keep' | 'update' | null 
  }>>(new Map());
  
  // 詳細抽出の初期結果を保存（既存データ適用前の状態）
  const [initialDetailedContracts, setInitialDetailedContracts] = useState<ExtractedContract[]>([]);
  
  // 既存データ適用前の契約データを契約IDごとに保存
  const [beforeExistingDataMap, setBeforeExistingDataMap] = useState<Map<number, ExtractedContract>>(new Map());
  
  // セッション内で確認済みの建物・家主データ
  const [sessionConfirmedData, setSessionConfirmedData] = useState<SessionConfirmedData>({
    buildings: []
  });
  
  // DB照合済みの契約IDを記録（各契約につき1回のみDB照合）
  const [dbCheckedContracts, setDbCheckedContracts] = useState<Set<number>>(new Set());

  // 既存データを適用する処理（建物と家主を個別に適用可能）
  const applyExistingData = useCallback((contractIdx: number, applyBuilding: boolean, applyOwner: boolean) => {
    console.log('applyExistingData called, contractIdx:', contractIdx, 'building:', applyBuilding, 'owner:', applyOwner);

    const checkResult = buildingChecks.get(contractIdx);
    if (!checkResult || !selectedContract) {
      console.log('Early return: no checkResult or selectedContract');
      return;
    }

    const { building, owner, session_temp_id } = checkResult;
    if (!building && applyBuilding) {
      console.log('Early return: no building data');
      return;
    }

    console.log('Applying existing data - building:', applyBuilding, 'owner:', applyOwner);

    // 復元用のデータソース：Mapから取得（保存された編集内容）
    // 保存はhandleBuildingResolutionChange/handleOwnerResolutionChangeで行われる
    let restoreSource: ExtractedContract;

    if (!applyBuilding || !applyOwner) {
      // 復元が必要な場合：保存された編集内容から取得
      const savedData = beforeExistingDataMap.get(selectedContract.id);
      if (savedData) {
        restoreSource = savedData;
        console.log('Restoring from saved data:', savedData);
      } else {
        // 保存データがない場合は初期抽出結果から取得
        restoreSource = initialDetailedContracts.find(c => c.id === selectedContract.id) || selectedContract;
        console.log('No saved data found, using initial or current data');
      }
    } else {
      // 既存データを適用する場合は復元不要
      restoreSource = selectedContract;
    }

    // 契約情報を更新
    let updatedContract: ExtractedContract = { ...selectedContract };

    // 建物情報の適用
    if (applyBuilding && building) {
      updatedContract = {
        ...updatedContract,
        session_building_temp_id: session_temp_id,
        property_name: building.name || updatedContract.property_name,
        building_code: { value: building.building_code, source_document: '既存データ' },
        building_name_kana: { value: building.name_kana, source_document: '既存データ' },
        property_postal_code: { value: building.postal_code, source_document: '既存データ' },
        property_address: { value: building.address1, source_document: '既存データ' },
        property_address2: { value: building.address2, source_document: '既存データ' },
      };
    } else if (!applyBuilding) {
      // 建物情報を保存された編集内容に戻す
      updatedContract = {
        ...updatedContract,
        session_building_temp_id: undefined,
        property_name: restoreSource.property_name,
        building_code: restoreSource.building_code,
        building_name_kana: restoreSource.building_name_kana,
        property_postal_code: restoreSource.property_postal_code,
        property_address: restoreSource.property_address,
        property_address2: restoreSource.property_address2,
      };
    }

    // 家主情報の適用
    if (applyOwner && owner) {
      updatedContract = {
        ...updatedContract,
        owner_name: { value: owner.name, source_document: '既存データ' },
        owner_name_kana: { value: owner.name_kana, source_document: '既存データ' },
        owner_postal_code: { value: owner.postal_code, source_document: '既存データ' },
        owner_address1: { value: owner.address1, source_document: '既存データ' },
        owner_address2: { value: owner.address2, source_document: '既存データ' },
        owner_home_phone: { value: owner.home_phone, source_document: '既存データ' },
        owner_mobile_phone: { value: owner.mobile_phone, source_document: '既存データ' },
        owner_bank_1_branch_code: { value: owner.bank_1_branch_code, source_document: '既存データ' },
        owner_bank_1_account_type: { value: owner.bank_1_account_type, source_document: '既存データ' },
        owner_bank_1_account_number: { value: owner.bank_1_account_number, source_document: '既存データ' },
        owner_bank_1_account_holder_name: { value: owner.bank_1_account_holder_name, source_document: '既存データ' },
        owner_bank_2_branch_code: { value: owner.bank_2_branch_code, source_document: '既存データ' },
        owner_bank_2_account_type: { value: owner.bank_2_account_type, source_document: '既存データ' },
        owner_bank_2_account_number: { value: owner.bank_2_account_number, source_document: '既存データ' },
        owner_bank_2_account_holder_name: { value: owner.bank_2_account_holder_name, source_document: '既存データ' },
      };
    } else if (!applyOwner) {
      // 家主情報を保存された編集内容に戻す
      updatedContract = {
        ...updatedContract,
        owner_name: restoreSource.owner_name,
        owner_name_kana: restoreSource.owner_name_kana,
        owner_type: restoreSource.owner_type,
        owner_postal_code: restoreSource.owner_postal_code,
        owner_address1: restoreSource.owner_address1,
        owner_address2: restoreSource.owner_address2,
        owner_home_phone: restoreSource.owner_home_phone,
        owner_mobile_phone: restoreSource.owner_mobile_phone,
        owner_bank_1_branch_code: restoreSource.owner_bank_1_branch_code,
        owner_bank_1_account_type: restoreSource.owner_bank_1_account_type,
        owner_bank_1_account_number: restoreSource.owner_bank_1_account_number,
        owner_bank_1_account_holder_name: restoreSource.owner_bank_1_account_holder_name,
        owner_bank_2_branch_code: restoreSource.owner_bank_2_branch_code,
        owner_bank_2_account_type: restoreSource.owner_bank_2_account_type,
        owner_bank_2_account_number: restoreSource.owner_bank_2_account_number,
        owner_bank_2_account_holder_name: restoreSource.owner_bank_2_account_holder_name,
      };
    }

    console.log('Updated contract:', updatedContract);

    // selectedContractを更新
    setSelectedContract(updatedContract);

    // aggregatedContractsとfinalContractsも更新
    setAggregatedContracts((prev) =>
      prev.map((c) => (c.id === selectedContract.id ? updatedContract : c))
    );
    setFinalContracts((prev) =>
      prev.map((c) => (c.id === selectedContract.id ? updatedContract : c))
    );

    console.log('applyExistingData completed successfully');
  }, [buildingChecks, selectedContract, initialDetailedContracts, beforeExistingDataMap, setSelectedContract, setAggregatedContracts, setFinalContracts]);

  // 建物の選択変更を処理
  const handleBuildingResolutionChange = useCallback((contractIdx: number, resolution: 'keep' | 'update') => {
    console.log('handleBuildingResolutionChange called, contractIdx:', contractIdx, 'resolution:', resolution);

    if (!selectedContract) return;

    // 現在の選択状態を取得（state更新前）
    const currentResolutions = conflictResolutions.get(contractIdx) || { building: null, owner: null };
    const previousBuildingResolution = currentResolutions.building;

    // 抽出データ→既存データへの切り替えの場合、現在のデータを保存
    if ((previousBuildingResolution === 'update' || previousBuildingResolution === null) && resolution === 'keep') {
      const currentData = { ...selectedContract };
      setBeforeExistingDataMap((prev) => {
        const newMap = new Map(prev);
        newMap.set(selectedContract.id, currentData);
        return newMap;
      });
      console.log('Saving current data before switching to existing data:', currentData);
    }

    // 既存データ→抽出データへの切り替えの場合、両方が抽出データに戻ったら保存をクリア
    if (previousBuildingResolution === 'keep' && resolution === 'update') {
      // 家主も抽出データの場合、保存をクリア
      if (currentResolutions.owner === 'update' || currentResolutions.owner === null) {
        setBeforeExistingDataMap((prev) => {
          const newMap = new Map(prev);
          newMap.delete(selectedContract.id);
          return newMap;
        });
        console.log('Cleared saved data when both building and owner switched back to extracted data');
      }
    }

    // 選択状態を更新
    setConflictResolutions((prev) => {
      const newMap = new Map(prev);
      newMap.set(contractIdx, { ...currentResolutions, building: resolution });
      return newMap;
    });

    // 建物の選択が変更されたので、必ず適用処理を実行
    // 家主の選択状態に応じて処理を決定
    const applyBuilding = resolution === 'keep';
    const applyOwner = currentResolutions.owner === 'keep';
    applyExistingData(contractIdx, applyBuilding, applyOwner);
  }, [conflictResolutions, applyExistingData, selectedContract]);

  // 家主の選択変更を処理
  const handleOwnerResolutionChange = useCallback((contractIdx: number, resolution: 'keep' | 'update') => {
    console.log('handleOwnerResolutionChange called, contractIdx:', contractIdx, 'resolution:', resolution);

    if (!selectedContract) return;

    // 現在の選択状態を取得（state更新前）
    const currentResolutions = conflictResolutions.get(contractIdx) || { building: null, owner: null };
    const previousOwnerResolution = currentResolutions.owner;

    // 抽出データ→既存データへの切り替えの場合、現在のデータを保存
    if ((previousOwnerResolution === 'update' || previousOwnerResolution === null) && resolution === 'keep') {
      const currentData = { ...selectedContract };
      setBeforeExistingDataMap((prev) => {
        const newMap = new Map(prev);
        newMap.set(selectedContract.id, currentData);
        return newMap;
      });
      console.log('Saving current data before switching to existing owner data:', currentData);
    }

    // 既存データ→抽出データへの切り替えの場合、両方が抽出データに戻ったら保存をクリア
    if (previousOwnerResolution === 'keep' && resolution === 'update') {
      // 建物も抽出データの場合、保存をクリア
      if (currentResolutions.building === 'update' || currentResolutions.building === null) {
        setBeforeExistingDataMap((prev) => {
          const newMap = new Map(prev);
          newMap.delete(selectedContract.id);
          return newMap;
        });
        console.log('Cleared saved data when both building and owner switched back to extracted data');
      }
    }

    // 選択状態を更新
    setConflictResolutions((prev) => {
      const newMap = new Map(prev);
      newMap.set(contractIdx, { ...currentResolutions, owner: resolution });
      return newMap;
    });

    // 家主の選択が変更されたので、必ず適用処理を実行
    // 建物の選択状態に応じて処理を決定
    const applyBuilding = currentResolutions.building === 'keep';
    const applyOwner = resolution === 'keep';
    applyExistingData(contractIdx, applyBuilding, applyOwner);
  }, [conflictResolutions, applyExistingData, selectedContract]);

  // 詳細抽出完了時に初期結果を保存
  useEffect(() => {
    if (flowState === "detailedExtracted" && finalContracts.length > 0 && initialDetailedContracts.length === 0) {
      console.log('Saving initial detailed contracts');
      setInitialDetailedContracts([...finalContracts]);
    }
  }, [flowState, finalContracts, initialDetailedContracts.length]);

  // 契約集約処理
  const lastAggregationKeyRef = useRef<string>("");

  useEffect(() => {
    if (finalContracts.length > 0) {
      // データの変更を検知するためのキーを作成
      const currentAggregationKey = JSON.stringify(finalContracts.map(c => ({
        id: c.id,
        property: c.property_name,
        room: c.room_name,
        lessee: c.lessee_name
      })));

      if (lastAggregationKeyRef.current !== currentAggregationKey) {
        lastAggregationKeyRef.current = currentAggregationKey;
        
        const aggregationMap = new Map<string, ExtractedContract>();
        finalContracts.forEach((contract) => {
          const key = `${contract.property_name}-${contract.room_name}-${contract.lessee_name}`;
          if (!aggregationMap.has(key)) {
            aggregationMap.set(key, contract);
          }
        });
        const aggregated = Array.from(aggregationMap.values());
        setAggregatedContracts(() => aggregated);
        
        // 選択中の契約が有効かチェックし、無効なら先頭を選択
        if (aggregated.length > 0) {
          const isSelectedValid = selectedContract && aggregated.some(c => c.id === selectedContract.id);
          if (!isSelectedValid) {
            setSelectedContract(aggregated[0]);
          }
        }
      }
    }
  }, [finalContracts, selectedContract, setAggregatedContracts, setSelectedContract]);

  // リセット関数
  const resetContractState = useCallback(() => {
    setConfirmedContracts(new Set());
    setNeedsReviewContracts(new Set());
    setAppliedExistingDataContracts(new Set());
    setConflictResolutions(new Map());
    setInitialDetailedContracts([]);
    setBeforeExistingDataMap(new Map());
    setSessionConfirmedData({ buildings: [] });
    setDbCheckedContracts(new Set());
  }, []);

  return {
    // 契約確認関連
    confirmedContracts,
    setConfirmedContracts,
    needsReviewContracts,
    setNeedsReviewContracts,
    appliedExistingDataContracts,
    setAppliedExistingDataContracts,
    
    // 重複・競合解決関連
    conflictResolutions,
    setConflictResolutions,
    initialDetailedContracts,
    setInitialDetailedContracts,
    beforeExistingDataMap,
    setBeforeExistingDataMap,
    sessionConfirmedData,
    setSessionConfirmedData,
    dbCheckedContracts,
    setDbCheckedContracts,
    
    // 関数
    applyExistingData,
    handleBuildingResolutionChange,
    handleOwnerResolutionChange,
    resetContractState,
  };
};